import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { productsAPI, exportsAPI } from "../services/api";
import { isInfluencerUser } from "../utils/auth";
import { getSellerAdminPath, getSellerName } from "../utils/seller";
import Pagination from "./Pagination";
import ExportCsvButton from "./ExportCsvButton";
import "./Products.css";

const isClosetProduct = (value) =>
  value === true || value === 1 || value === "1";

const getProductDisplayStatus = (product) => {
  const status = (product?.status || "default").toLowerCase();
  const expiresAt = product?.expires_at ? new Date(product.expires_at) : null;
  const isExpired = !!(
    expiresAt &&
    !Number.isNaN(expiresAt.getTime()) &&
    expiresAt < new Date() &&
    status !== "sold" &&
    status !== "rejected"
  );
  return isExpired ? "expired" : status;
};

const getPrimaryImage = (product) => {
  const imgs = product?.images;
  if (!Array.isArray(imgs) || imgs.length === 0) return null;
  const sorted = [...imgs].sort(
    (a, b) => (a.image_order ?? 0) - (b.image_order ?? 0)
  );
  const primary = sorted.find((i) => i.is_primary) || sorted[0];
  return primary?.image_url || null;
};

const formatExpiry = (value) => {
  if (!value) return "No expiry";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const initialPerPage = () => {
  const stored = parseInt(localStorage.getItem("per_page") || "", 10);
  return [10, 15, 25, 50, 100].includes(stored) ? stored : 15;
};

const Products = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sellerIdFilter = searchParams.get("seller_id") || "";
  const sellerNameFilter = searchParams.get("seller_name") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: initialPerPage(),
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevDebouncedSearch = useRef("");
  const isInfluencer = isInfluencerUser();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (statusFilter && statusFilter !== "expired") params.status = statusFilter;
      if (typeFilter !== "") {
        params.is_closet_product = typeFilter === "closet";
      }
      if (debouncedSearch) params.search = debouncedSearch;
      if (sellerIdFilter) params.seller_id = sellerIdFilter;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;

      const response = await productsAPI.getAll(params);
      let nextProducts = response.data.data || [];

      if (statusFilter === "expired") {
        nextProducts = nextProducts.filter(
          (p) => getProductDisplayStatus(p) === "expired"
        );
      }
      if (dateFromFilter || dateToFilter) {
        nextProducts = nextProducts.filter((p) => {
          if (!p?.created_at) return false;
          const date = new Date(p.created_at);
          if (Number.isNaN(date.getTime())) return false;
          const createdDate = date.toISOString().slice(0, 10);
          if (dateFromFilter && createdDate < dateFromFilter) return false;
          if (dateToFilter && createdDate > dateToFilter) return false;
          return true;
        });
      }

      setProducts(nextProducts);
      const pg = response.data.pagination;
      if (pg) {
        setPagination((prev) => {
          const nextPage = pg.page ?? prev.page;
          const nextPerPage = pg.per_page ?? prev.per_page;
          const nextTotal = pg.total ?? 0;
          if (
            prev.page === nextPage &&
            prev.per_page === nextPerPage &&
            prev.total === nextTotal
          ) {
            return prev;
          }
          return { ...prev, page: nextPage, per_page: nextPerPage, total: nextTotal };
        });
      }
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error(
        "Error loading products: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.per_page,
    statusFilter,
    typeFilter,
    debouncedSearch,
    sellerIdFilter,
    dateFromFilter,
    dateToFilter,
  ]);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  const resetPage = () => setPagination((p) => ({ ...p, page: 1 }));

  const clearSellerFilter = () => {
    setSearchParams((prev) => {
      prev.delete("seller_id");
      prev.delete("seller_name");
      return prev;
    });
    resetPage();
  };

  const handleSellerClick = (e, seller) => {
    e.stopPropagation();
    const path = getSellerAdminPath(seller);
    if (path) navigate(path);
  };

  const handleViewDetails = (id) => navigate(`/products/${id}`);

  const handleEdit = (id) => navigate(`/products/${id}/edit`);

  const handleDuplicate = async (id) => {
    if (!window.confirm("Duplicate this product? A pending copy will be created.")) return;
    try {
      const response = await productsAPI.duplicate(id);
      const newId = response.data.product?.id;
      toast.success("Product duplicated");
      if (newId) {
        navigate(`/products/${newId}/edit`);
      } else {
        void fetchProducts();
      }
    } catch (error) {
      toast.error(
        "Error duplicating product: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleApprove = async (id) => {
    try {
      await productsAPI.approve(id);
      void fetchProducts();
      toast.success("Product approved!");
    } catch (error) {
      toast.error(
        "Error approving product: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this product?")) return;
    try {
      await productsAPI.reject(id);
      void fetchProducts();
      toast.success("Product rejected!");
    } catch (error) {
      toast.error(
        "Error rejecting product: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await productsAPI.delete(id);
      void fetchProducts();
      toast.success("Product deleted!");
    } catch (error) {
      toast.error(
        "Error deleting product: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleExtendExpiry = async (product) => {
    const productId = product.id || product.uuid;
    const baseDate = product.expires_at ? new Date(product.expires_at) : new Date();
    const nextDate = new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    try {
      await productsAPI.update(productId, {
        expires_at: nextDate.toISOString(),
      });
      toast.success("Expiration extended by 7 days");
      void fetchProducts();
    } catch (error) {
      toast.error(
        "Error extending expiration: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setSearchInput("");
    setDebouncedSearch("");
    prevDebouncedSearch.current = "";
    clearSellerFilter();
  };

  const handleAddNew = () => navigate("/products/new");

  if (loading && products.length === 0) {
    return <div className="loading">Loading products…</div>;
  }

  const totalLabel =
    pagination.total > 0
      ? `${pagination.total} product${pagination.total === 1 ? "" : "s"}`
      : "No products";

  return (
    <div className="products-page">
      <div className="page-toolbar">
        <p className="page-meta">{loading ? "Loading…" : totalLabel}</p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <ExportCsvButton
            exportRequest={exportsAPI.products}
            filters={{
              search: debouncedSearch || undefined,
              status: statusFilter || undefined,
              is_closet_product:
                typeFilter !== "" ? typeFilter === "closet" : undefined,
              seller_id: sellerIdFilter || undefined,
              date_from: dateFromFilter || undefined,
              date_to: dateToFilter || undefined,
            }}
            filename="products.csv"
          />
          {!isInfluencer ? (
            <button type="button" className="btn btn-success" onClick={handleAddNew}>
              + New product
            </button>
          ) : null}
        </div>
      </div>

      {sellerIdFilter ? (
        <div className="products-seller-banner">
          <span>
            Listings for{" "}
            <strong>{sellerNameFilter || "this customer"}</strong>
          </span>
          <button type="button" className="btn-secondary btn-sm" onClick={clearSellerFilter}>
            Clear seller filter
          </button>
        </div>
      ) : null}

      <div className="products-filters">
        <div className="products-filter-field products-filter-field--grow">
          <label htmlFor="products-search">Keyword</label>
          <input
            id="products-search"
            type="search"
            placeholder="Title, brand, or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="products-filter-field">
          <label htmlFor="products-status">Status</label>
          <select
            id="products-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              resetPage();
            }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="sold">Sold</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        {!isInfluencer ? (
          <div className="products-filter-field">
            <label htmlFor="products-type">Type</label>
            <select
              id="products-type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                resetPage();
              }}
            >
              <option value="">All types</option>
              <option value="closet">Closet</option>
              <option value="marketplace">Marketplace</option>
            </select>
          </div>
        ) : null}
        <div className="products-filter-field">
          <label htmlFor="products-from">Created from</label>
          <input
            id="products-from"
            type="date"
            value={dateFromFilter}
            onChange={(e) => {
              setDateFromFilter(e.target.value);
              resetPage();
            }}
          />
        </div>
        <div className="products-filter-field">
          <label htmlFor="products-to">Created to</label>
          <input
            id="products-to"
            type="date"
            value={dateToFilter}
            onChange={(e) => {
              setDateToFilter(e.target.value);
              resetPage();
            }}
          />
        </div>
        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
          Clear
        </button>
      </div>

      {loading && products.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      {products.length === 0 ? (
        <div className="products-empty">
          No products match your filters.
          {!isInfluencer ? (
            <>
              {" "}
              <button type="button" className="btn btn-primary btn-sm" onClick={handleAddNew}>
                Create a product
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="products-list">
          {products.map((product) => {
            const productId = product.id || product.uuid;
            const displayStatus = getProductDisplayStatus(product);
            const thumb = getPrimaryImage(product);
            const closet = isClosetProduct(product.is_closet_product);
            const sellerName = getSellerName(product.seller);
            const sellerPath = getSellerAdminPath(product.seller);

            return (
              <article key={productId} className="product-card">
                <div
                  className="product-card-thumb"
                  onClick={() => handleViewDetails(productId)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleViewDetails(productId)
                  }
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${product.title}`}
                >
                  {thumb ? (
                    <img src={thumb} alt="" />
                  ) : (
                    <span className="product-card-thumb-placeholder">No photo</span>
                  )}
                </div>

                <div
                  className="product-card-main"
                  onClick={() => handleViewDetails(productId)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleViewDetails(productId)
                  }
                  role="button"
                  tabIndex={0}
                >
                  <h3 className="product-card-title">{product.title}</h3>
                  <div className="product-card-meta">
                    <span className="product-card-price">
                      {product.currency || "AED"} {product.price}
                    </span>
                    <span className={`status-badge status-${displayStatus}`}>
                      {displayStatus}
                    </span>
                    <span
                      className={`product-type-pill ${closet ? "closet" : ""}`}
                    >
                      {closet ? "Closet" : "Marketplace"}
                    </span>
                  </div>
                  <div className="product-card-sub">
                    <span>{product.brand || "No brand"}</span>
                    <span>{product.category?.name || "Uncategorized"}</span>
                    {product.seller?.id && sellerPath ? (
                      <button
                        type="button"
                        className="product-card-seller-link"
                        onClick={(e) => handleSellerClick(e, product.seller)}
                      >
                        {sellerName}
                      </button>
                    ) : (
                      <span>Seller {sellerName}</span>
                    )}
                    <span>Expires {formatExpiry(product.expires_at)}</span>
                  </div>
                </div>

                <div className="product-card-actions">
                  <button
                    type="button"
                    className="btn-outline btn-sm"
                    onClick={() => handleViewDetails(productId)}
                  >
                    View
                  </button>
                  {!isInfluencer ? (
                    <>
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => handleEdit(productId)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => handleDuplicate(productId)}
                      >
                        Duplicate
                      </button>
                      {product.status === "pending" ? (
                        <>
                          <button
                            type="button"
                            className="btn-success btn-sm"
                            onClick={() => handleApprove(productId)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-danger btn-sm"
                            onClick={() => handleReject(productId)}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => handleExtendExpiry(product)}
                      >
                        +7d
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-sm"
                        onClick={() => handleDelete(productId)}
                      >
                        Delete
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pagination.total > pagination.per_page ? (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
          onPerPageChange={(per_page) =>
            setPagination((p) => ({ ...p, per_page, page: 1 }))
          }
        />
      ) : null}
    </div>
  );
};

export default Products;
